import {
  PROMPT_REPORT_LABELS,
  type Persona,
  type ProcessingJobList,
  type PromptReportType,
} from "./api";

export interface PersonaTab {
  type: PromptReportType;
  label: string;
}

const DEFAULT_TABS: PersonaTab[] = [
  { type: "SUMMARY", label: "Summary" },
  { type: "TRANSCRIPT", label: "Transcript" },
];

/** Persona → ordered list of report_type values that should appear as tabs.
 *  Always end with TRANSCRIPT (always-available); for "transcript" persona
 *  only TRANSCRIPT is shown.
 *
 *  This matches the backend's `services.prompt_reports.persona_prompts.PERSONA_REPORTS`
 *  ordering. The display labels come from the job's `report_labels` field at runtime,
 *  with PROMPT_REPORT_LABELS as a fallback.
 */
const PERSONA_TYPE_ORDER: Record<Persona, PromptReportType[]> = {
  auto: ["SUMMARY", "TRANSCRIPT"],
  podcaster: ["SUMMARY", "SHOW_NOTES", "KEY_QUOTES", "BLOG_DRAFT", "TRANSCRIPT"],
  youtuber: ["SUMMARY", "CHAPTERS", "KEY_MOMENTS", "DESCRIPTION_TAGS", "TRANSCRIPT"],
  journalist: ["SUMMARY", "ARTICLE", "KEY_QUOTES", "FACT_CHECK", "TRANSCRIPT"],
  lecturer: ["SUMMARY", "STUDY_NOTES", "KEY_CONCEPTS", "QUIZ", "TRANSCRIPT"],
  business: ["SUMMARY", "ACTION_ITEMS", "DECISION_LOG", "EXECUTIVE_BRIEF", "TRANSCRIPT"],
  // Education aliases (same tabs as lecturer)
  curator: ["SUMMARY", "STUDY_NOTES", "KEY_CONCEPTS", "QUIZ", "TRANSCRIPT"],
  student: ["SUMMARY", "STUDY_NOTES", "KEY_CONCEPTS", "QUIZ", "TRANSCRIPT"],
  teacher: ["SUMMARY", "STUDY_NOTES", "KEY_CONCEPTS", "QUIZ", "TRANSCRIPT"],
  methodist: ["SUMMARY", "STUDY_NOTES", "KEY_CONCEPTS", "QUIZ", "TRANSCRIPT"],
  // Business aliases (same tabs as business)
  consultant: ["SUMMARY", "ACTION_ITEMS", "DECISION_LOG", "EXECUTIVE_BRIEF", "TRANSCRIPT"],
  ea_cos: ["SUMMARY", "ACTION_ITEMS", "DECISION_LOG", "EXECUTIVE_BRIEF", "TRANSCRIPT"],
  executive: ["SUMMARY", "ACTION_ITEMS", "DECISION_LOG", "EXECUTIVE_BRIEF", "TRANSCRIPT"],
  ld_training: ["SUMMARY", "ACTION_ITEMS", "DECISION_LOG", "EXECUTIVE_BRIEF", "TRANSCRIPT"],
  manager: ["SUMMARY", "ACTION_ITEMS", "DECISION_LOG", "EXECUTIVE_BRIEF", "TRANSCRIPT"],
  sales_cs: ["SUMMARY", "ACTION_ITEMS", "DECISION_LOG", "EXECUTIVE_BRIEF", "TRANSCRIPT"],
  // Science aliases (same tabs as lecturer)
  journal_editor: ["SUMMARY", "STUDY_NOTES", "KEY_CONCEPTS", "QUIZ", "TRANSCRIPT"],
  peer_reviewer: ["SUMMARY", "STUDY_NOTES", "KEY_CONCEPTS", "QUIZ", "TRANSCRIPT"],
  phd_postdoc: ["SUMMARY", "STUDY_NOTES", "KEY_CONCEPTS", "QUIZ", "TRANSCRIPT"],
  researcher: ["SUMMARY", "STUDY_NOTES", "KEY_CONCEPTS", "QUIZ", "TRANSCRIPT"],
  sci_communicator: ["SUMMARY", "STUDY_NOTES", "KEY_CONCEPTS", "QUIZ", "TRANSCRIPT"],
  transcript: ["TRANSCRIPT"],
};

export function getPersonaTabs(
  persona: Persona | undefined,
  reportLabels?: Record<string, string>,
): PersonaTab[] {
  if (!persona) return DEFAULT_TABS;
  const order = PERSONA_TYPE_ORDER[persona] ?? PERSONA_TYPE_ORDER.business;
  return order.map((type) => ({
    type,
    label: reportLabels?.[type] ?? PROMPT_REPORT_LABELS[type],
  }));
}

/** Convenience: derive tabs straight from a job. */
export function getTabsForJob(job: ProcessingJobList | undefined): PersonaTab[] {
  if (!job) return DEFAULT_TABS;
  return getPersonaTabs(job.persona, job.report_labels);
}
